/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 *
 * What an alert configuration's settings are called, and what kind of value each one holds.
 *
 * An alert configuration is created from one of ~24 behaviour templates, and every template
 * declares its settings — label, type, and the words for a boolean's two states — in an
 * `alertConfigSettings` block. That block lives in CouchDB, which api-insights cannot reach: the
 * Azure network does not route to it, and adding the dependency would make audit rendering fail
 * wherever Couch is unreachable. So the table is transcribed here instead of fetched.
 *
 * Two facts make a static copy safe rather than a guess:
 *
 *  - Settings are keyed globally, not per behaviour. Across all 24 behaviours, 45 of the 48 keys
 *    carry exactly one label, and the three that vary differ only by the word "Virtual". A
 *    configuration's diff can therefore be labelled without knowing which behaviour produced it —
 *    which matters, because `alertBehaviorId` appears only in a `create` diff and Fresh's
 *    `AlertConfigs` table does not record it, so on an update the behaviour is genuinely unknown.
 *  - Boolean option labels never vary by behaviour: `doorOpen` is always open/closed.
 *
 * Regenerate after a behaviour changes:
 *   curl -su "$COUCHDB_USER:$COUCHDB_PASS" \
 *     "$COUCHDB_URL/alert-behaviors-v2/_all_docs?include_docs=true"
 * then read `alertConfigSettings[].key / .label / .type / .trueOptionLabel / .falseOptionLabel`.
 * An unlisted key is not a failure — it falls back to a humanised reading of the key itself.
 *
 * This file is deliberately duplicated in api-insights (`src/api/audits/alertSettings.ts`), which
 * needs the same table to build the one-line summary shown in the list. The two are separate
 * deployables with no shared package; regenerate both together.
 */

/** The value kinds a setting can hold, as the behaviour templates name them. */
export type SettingType =
  | 'Boolean'
  | 'Duration'
  | 'FridgeAlarms'
  | 'Number'
  | 'OperatingHours'
  | 'Percent'
  | 'Severity'
  | 'Speed'
  | 'String'
  | 'StringList'
  | 'Temperature'
  | 'TemperatureDelta'
  | 'TemperatureRange'

/** Setting key → the label the alert configuration screen puts beside it. */
export const SETTING_LABELS: Record<string, string | undefined> = {
  repeatInterval: 'Repeat interval',
  raiseDelay: 'Raise delay',
  operatingHours: 'Operating hours',
  clearDelay: 'Clear delay',
  outsideEveryGeofence: 'Outside all of these geofences',
  outsideEveryGeofenceCategory: 'Outside all of these geofence categories',
  severity: 'Severity',
  inAnyGeofence: 'Inside any of these geofences',
  inAnyGeofenceCategory: 'Inside any of these geofence categories',
  alertAcknowledgement: 'Alert acknowledgement',
  shouldHideGoToGraphLink: 'Hide go to graph link',
  onTrip: 'Trip status',
  enabledAlarmsKeys: 'Fridge alarms',
  reeferPowerOn: 'Reefer power',
  doorMonitoringPointNames: 'Door monitoring point names',
  zone: 'Zone',
  automaticallyClearAfter: 'Automatically clear after',
  isInMotion: 'Motion',
  offlineDelay: 'Duration till deemed offline',
  batteryVoltageLowerLimit: 'Reefer battery lower limit (V)',
  criticalThreshold: 'Critical threshold',
  warningThreshold: 'Warning threshold',
  destinationGeofences: 'Destination geofences',
  destinationGeofenceCategories: 'Destination geofence categories',
  doorOpen: 'Door',
  entersThisGeofenceCategory: 'Geofence category',
  exitsThisGeofenceCategory: 'Geofence category',
  excludedGeofences: 'Excluded geofences',
  fridgeOffGracePeriod: 'Disable alerts if fridge is off longer than',
  highThreshold: 'High threshold',
  lowThreshold: 'Low threshold',
  inAnyOfTheseGeofences: 'Inside any of these geofences',
  insideAnyGeofence: 'Inside any of these geofences',
  insideAnyGeofenceCategory: 'Inside any of these geofence categories',
  insideAnyOfTheseGeofences: 'Inside any of these geofences',
  insideAnyOfTheseGeofenceCategories: 'Inside any of these geofence categories',
  outsideAllOfTheseGeofences: 'Outside all of these geofences',
  outsideAllOfTheseGeofenceCategories: 'Outside all of these geofence categories',
  outsideAnyOfTheseGeofences: 'Outside any of these geofences',
  outsideAnyOfTheseGeofenceCategories: 'Outside any of these geofence categories',
  reeferOperationMode: 'Reefer operational mode',
  setpoint: 'Setpoint',
  lowerSetpoint: 'Lower setpoint',
  upperSetpoint: 'Upper setpoint',
  speed: 'Speed threshold',
  threshold: 'Threshold',
  setpointRangeFocus: 'Setpoint range focus',
  lowerSetpointTolerance: 'Lower virtual setpoint tolerance',
  upperSetpointTolerance: 'Upper virtual setpoint tolerance',

  // Retired with `alert-behavior:Geofence entry/ver1.0.0`, which no current template replaces. A
  // handful of live configurations still carry these keys, and their audit history keeps them
  // forever, so they are labelled here rather than left to the humanised fallback.
  entersAnyOfTheseGeofences: 'Enters any of these geofences',
  clearOnGeofenceExit: 'Clear on geofence exit',
}

/** Setting key → value kind, so a value renders as what it is rather than as a bare number. */
export const SETTING_TYPES: Record<string, SettingType | undefined> = {
  repeatInterval: 'Duration',
  raiseDelay: 'Duration',
  clearDelay: 'Duration',
  automaticallyClearAfter: 'Duration',
  offlineDelay: 'Duration',
  fridgeOffGracePeriod: 'Duration',
  operatingHours: 'OperatingHours',
  severity: 'Severity',
  enabledAlarmsKeys: 'FridgeAlarms',
  alertAcknowledgement: 'Boolean',
  shouldHideGoToGraphLink: 'Boolean',
  onTrip: 'Boolean',
  reeferPowerOn: 'Boolean',
  isInMotion: 'Boolean',
  doorOpen: 'Boolean',
  criticalThreshold: 'Percent',
  warningThreshold: 'Percent',
  batteryVoltageLowerLimit: 'Number',
  speed: 'Speed',
  setpoint: 'Temperature',
  lowerSetpoint: 'Temperature',
  upperSetpoint: 'Temperature',
  highThreshold: 'Temperature',
  lowThreshold: 'Temperature',
  threshold: 'TemperatureDelta',
  lowerSetpointTolerance: 'TemperatureDelta',
  upperSetpointTolerance: 'TemperatureDelta',
  setpointRangeFocus: 'TemperatureRange',
  zone: 'String',
  reeferOperationMode: 'String',
  entersThisGeofenceCategory: 'String',
  exitsThisGeofenceCategory: 'String',
  doorMonitoringPointNames: 'StringList',
  outsideEveryGeofence: 'StringList',
  outsideEveryGeofenceCategory: 'StringList',
  inAnyGeofence: 'StringList',
  inAnyGeofenceCategory: 'StringList',
  insideAnyGeofence: 'StringList',
  insideAnyGeofenceCategory: 'StringList',
  inAnyOfTheseGeofences: 'StringList',
  insideAnyOfTheseGeofences: 'StringList',
  insideAnyOfTheseGeofenceCategories: 'StringList',
  outsideAllOfTheseGeofences: 'StringList',
  outsideAllOfTheseGeofenceCategories: 'StringList',
  outsideAnyOfTheseGeofences: 'StringList',
  outsideAnyOfTheseGeofenceCategories: 'StringList',
  destinationGeofences: 'StringList',
  destinationGeofenceCategories: 'StringList',
  excludedGeofences: 'StringList',
  entersAnyOfTheseGeofences: 'StringList',
  clearOnGeofenceExit: 'Boolean',
}

/**
 * The words a boolean setting's two states are shown as.
 *
 * This is the difference between a row that misleads and one that informs: `doorOpen` set to false
 * is "Door closed", never "Door disabled", and `isInMotion` false is "Stationary" — a state, not an
 * absence. A boolean setting not listed here keeps the generic enabled/disabled reading.
 */
export const SETTING_BOOLEAN_LABELS: Record<string, { true: string; false: string } | undefined> = {
  doorOpen: { true: 'open', false: 'closed' },
  isInMotion: { true: 'moving', false: 'stationary' },
  onTrip: { true: 'on trip', false: 'not on trip' },
  reeferPowerOn: { true: 'on', false: 'off' },
  // `alertAcknowledgement` is deliberately absent: the template calls its states enable/disable,
  // which is already the generic reading, and listing it would turn "Alert acknowledgement enabled"
  // into the clumsier "Alert acknowledgement set to enabled".
}

export const SPEED_CATEGORY_LABELS: Record<string, string | undefined> = {
  max: 'Maximum speed',
  average: 'Average speed',
}

/**
 * The "only inside/outside this geofence" checkbox that sits under a geofence setting.
 *
 * Keyed checkbox → the setting it belongs to. The names do not derive from one another —
 * `insideAnyGeofence` pairs with `alertWhenOnlyInsideGeofence*s*` — so the pairing is transcribed
 * rather than computed.
 */
export const SETTING_FOR_CHECKBOX: Record<string, string | undefined> = {
  alertWhenOnlyInsideGeofences: 'insideAnyGeofence',
  alertWhenOnlyInsideGeofenceCategories: 'insideAnyGeofenceCategory',
  alertWhenOnlyOutsideGeofence: 'outsideEveryGeofence',
  alertWhenOnlyOutsideGeofenceCategories: 'outsideEveryGeofenceCategory',
}

/** What each checkbox says on the alert configuration screen. */
export const CHECKBOX_LABELS: Record<string, string | undefined> = {
  alertWhenOnlyInsideGeofences: 'Alert only inside geofence',
  alertWhenOnlyInsideGeofenceCategories: 'Alert only inside geofence categories',
  alertWhenOnlyOutsideGeofence: 'Alert only outside geofence',
  alertWhenOnlyOutsideGeofenceCategories: 'Alert only outside geofence categories',
}

/**
 * What the ids in a list setting point at, so a reader can be shown names instead.
 *
 * `insideAnyGeofence` and `insideAnyGeofenceCategory` carry no `optionType` in the behaviour
 * document — an omission in the Door template rather than a different kind of value — so they are
 * classified here by what they demonstrably hold.
 */
export const SETTING_OPTION_TYPES: Record<string, 'Geofence' | 'GeofenceCategory' | 'Zone' | undefined> = {
  inAnyGeofence: 'Geofence',
  inAnyOfTheseGeofences: 'Geofence',
  insideAnyGeofence: 'Geofence',
  insideAnyOfTheseGeofences: 'Geofence',
  outsideEveryGeofence: 'Geofence',
  outsideAllOfTheseGeofences: 'Geofence',
  outsideAnyOfTheseGeofences: 'Geofence',
  destinationGeofences: 'Geofence',
  excludedGeofences: 'Geofence',
  inAnyGeofenceCategory: 'GeofenceCategory',
  insideAnyGeofenceCategory: 'GeofenceCategory',
  insideAnyOfTheseGeofenceCategories: 'GeofenceCategory',
  outsideEveryGeofenceCategory: 'GeofenceCategory',
  outsideAllOfTheseGeofenceCategories: 'GeofenceCategory',
  outsideAnyOfTheseGeofenceCategories: 'GeofenceCategory',
  destinationGeofenceCategories: 'GeofenceCategory',
  entersThisGeofenceCategory: 'GeofenceCategory',
  exitsThisGeofenceCategory: 'GeofenceCategory',
  entersAnyOfTheseGeofences: 'Geofence',
  zone: 'Zone',
}

const MINUTE = 60_000
const HOUR = 60 * MINUTE
const DAY = 24 * HOUR

const plural = (count: number, unit: string): string => `${String(count)} ${unit}${count === 1 ? '' : 's'}`

/**
 * A duration in milliseconds, as a person would say it.
 *
 * Every `Duration` setting is stored in milliseconds, so a raise delay reads as `120000` in the raw
 * diff — a number nobody can convert at a glance while reading an audit trail. Values that are not
 * whole units keep their millisecond form rather than being rounded into a wrong answer.
 */
export const formatDuration = (value: number): string => {
  if (!Number.isFinite(value)) return String(value)
  if (value === 0) return '0'

  const absolute = Math.abs(value)
  if (absolute % DAY === 0) return plural(value / DAY, 'day')
  if (absolute % HOUR === 0) return plural(value / HOUR, 'hour')
  if (absolute % MINUTE === 0) return plural(value / MINUTE, 'minute')
  if (absolute % 1000 === 0) return plural(value / 1000, 'second')

  return `${String(value)} ms`
}
